import { useCallback, useRef } from "react"

import { useUpdateAnswerPlacement } from "@/services/api/questionnaireService"
import {
  LayoutRect,
  QuestionnaireResponsePair,
  UserPlacement,
} from "@/types/questionnaire/models"
import { debounce, type DebouncedFunction } from "@/utils/optimisationUtils"

import { safeParseStringArray } from "@/utils/questionnaire/optionText"
import {
  getResponsePairIdFromBoxId,
  OptionBoxMeta,
  parseOptionBoxId,
} from "../utils/boxIdUtils"
import {
  getOptionsPlacements,
  updateOptionPlacement,
} from "../utils/optionsPlacementUtils"
import {
  AnswerPlacementPayload,
  ensureUserPlacement,
  stringifyStringArray,
  toApiPlacement,
} from "../utils/placementUtils"

type QuestionUpdater = (
  question: QuestionnaireResponsePair,
) => QuestionnaireResponsePair

export const usePlacementUpdater = (
  questionnaireId: string,
  updateResponsePairById: (
    responsePairId: string,
    updater: QuestionUpdater,
  ) => void,
) => {
  const { mutateAsync: updateAnswerPlacementAsync } = useUpdateAnswerPlacement()

  // Tracks every placement save currently in flight (debounced or immediate) so
  // flush() can await them all before the caller navigates away — a save that
  // already started must finish, and a save that hasn't started yet (still
  // sitting in the debounce) must be forced to start, before we leave the page.
  const inFlightSavesRef = useRef<Set<Promise<unknown>>>(new Set())

  // Tracks response pair ids whose most recent placement save rejected. A
  // rejected save is removed from inFlightSavesRef once it settles, so a
  // later flush() with no new edit would otherwise see nothing in flight and
  // wrongly proceed — losing the displayed-but-unsaved edit. Keeping the id
  // here until a subsequent save for the same pair succeeds keeps flush()
  // rejecting (and the existing error toast showing) until the edit actually
  // persists.
  const failedSavesRef = useRef<Set<string>>(new Set())

  const runUpdateAnswerPlacement = useCallback(
    (
      questionnaireId: string,
      responsePairId: string,
      placement: AnswerPlacementPayload,
    ) => {
      const promise = updateAnswerPlacementAsync({
        questionnaireId,
        responsePairId,
        placement,
      })
      inFlightSavesRef.current.add(promise)
      // Swallow here so a failed save doesn't surface as an unhandled rejection
      // when nobody calls flush(); flush()'s own await still observes it.
      promise
        .then(() => {
          failedSavesRef.current.delete(responsePairId)
        })
        .catch(() => {
          failedSavesRef.current.add(responsePairId)
        })
        .finally(() => {
          inFlightSavesRef.current.delete(promise)
        })
    },
    [updateAnswerPlacementAsync],
  )

  // Keep the latest runner in a ref so the debounced function below — created
  // exactly once for the hook's lifetime — always calls into current save
  // logic without ever being re-created itself. Re-creating it (e.g. via a
  // useMemo keyed on runUpdateAnswerPlacement) would drop any call already
  // pending on the old instance whenever updateAnswerPlacementAsync's
  // identity changes between the edit and flush() — silently losing the
  // edit instead of persisting it.
  const runUpdateAnswerPlacementRef = useRef(runUpdateAnswerPlacement)
  runUpdateAnswerPlacementRef.current = runUpdateAnswerPlacement

  const debouncedUpdatePlacementRef = useRef<DebouncedFunction<
    [string, string, AnswerPlacementPayload]
  > | null>(null)

  if (!debouncedUpdatePlacementRef.current) {
    debouncedUpdatePlacementRef.current = debounce(
      (
        questionnaireId: string,
        responsePairId: string,
        placement: AnswerPlacementPayload,
      ) => {
        runUpdateAnswerPlacementRef.current(
          questionnaireId,
          responsePairId,
          placement,
        )
      },
      500,
    )
  }

  const debouncedUpdatePlacement = debouncedUpdatePlacementRef.current

  // Forces the pending debounced save (if any) to run now, then waits for every
  // placement save currently in flight. Rejects if any of them fail — or if an
  // earlier save is still unpersisted from failedSavesRef — so the caller can
  // keep the user on the page instead of navigating on a lost edit.
  const flush = useCallback(async () => {
    debouncedUpdatePlacement.flush()
    await Promise.all(Array.from(inFlightSavesRef.current))
    if (failedSavesRef.current.size > 0) {
      throw new Error("Unsaved answer placement edits could not be saved")
    }
  }, [debouncedUpdatePlacement])

  const updatePlacementForOptions = useCallback(
    (
      question: QuestionnaireResponsePair,
      optionMeta: OptionBoxMeta,
      updates: Partial<{ x: number; y: number; width: number; height: number }>,
    ): QuestionnaireResponsePair => {
      const newQuestion = { ...question }
      newQuestion.pdfPlacement = { ...question.pdfPlacement }

      const extractionHint = question.pdfPlacement?.extraction?.answerBboxHint
      const extractionFontSize = question.pdfPlacement?.extraction?.fontSize
      const existingUp = newQuestion.pdfPlacement.userPlacement

      const optionsPlacements = getOptionsPlacements(question, existingUp)

      if (!optionsPlacements[optionMeta.optionIndex]) {
        return question
      }

      const updatedOptions = updateOptionPlacement(
        optionsPlacements,
        optionMeta.optionIndex,
        updates,
      )

      const up = ensureUserPlacement(
        existingUp,
        extractionHint
          ? { ...extractionHint, fontSize: extractionFontSize }
          : extractionHint,
        question.response || "",
        { optionsPlacements: updatedOptions },
      )

      newQuestion.pdfPlacement.userPlacement = up

      const responseText =
        question.type === "multiple-select"
          ? stringifyStringArray(safeParseStringArray(question.response || ""))
          : question.response || ""

      debouncedUpdatePlacement(questionnaireId, question.id, {
        ...toApiPlacement(up),
        text: responseText,
        options_placements: updatedOptions,
      })

      return newQuestion
    },
    [debouncedUpdatePlacement, questionnaireId],
  )

  const updatePlacementForAnswer = useCallback(
    (
      question: QuestionnaireResponsePair,
      updates: Partial<UserPlacement>,
      immediate = false,
    ): QuestionnaireResponsePair => {
      const newQuestion = { ...question }
      newQuestion.pdfPlacement = { ...question.pdfPlacement }

      const answerHint = question.pdfPlacement?.extraction?.answerBboxHint
      const questionBbox = question.pdfPlacement?.extraction?.questionBbox
      // When answer hint is missing, seed the placement next to the question
      // bbox so the box lands on the correct page (mirrors the rendering
      // fallback in useHighlightBoxes for choice questions without options).
      const extractionHint =
        answerHint ??
        (questionBbox
          ? {
              page: questionBbox.page,
              x: questionBbox.x + questionBbox.w + 2,
              y: questionBbox.y,
              w: 120,
              h: questionBbox.h,
            }
          : null)
      const extractionFontSize = question.pdfPlacement?.extraction?.fontSize
      const existingUp = newQuestion.pdfPlacement.userPlacement

      const up = ensureUserPlacement(
        existingUp,
        extractionHint
          ? { ...extractionHint, fontSize: extractionFontSize }
          : extractionHint,
        question.response || "",
        updates,
      )

      newQuestion.pdfPlacement.userPlacement = up

      const apiPlacement = toApiPlacement(up)

      if (immediate) {
        runUpdateAnswerPlacement(questionnaireId, question.id, apiPlacement)
      } else {
        debouncedUpdatePlacement(questionnaireId, question.id, apiPlacement)
      }

      return newQuestion
    },
    [debouncedUpdatePlacement, questionnaireId, runUpdateAnswerPlacement],
  )

  const handleDragEnd = useCallback(
    (id: string, x: number, y: number) => {
      const optionMeta = parseOptionBoxId(id)
      const responsePairId = optionMeta
        ? optionMeta.questionId
        : getResponsePairIdFromBoxId(id)

      updateResponsePairById(responsePairId, (question) => {
        if (
          optionMeta &&
          (question.type === "boolean" ||
            question.type === "multiple-choice" ||
            question.type === "multiple-select")
        ) {
          return updatePlacementForOptions(question, optionMeta, { x, y })
        }

        return updatePlacementForAnswer(question, { x, y }, true)
      })
    },
    [
      updatePlacementForAnswer,
      updatePlacementForOptions,
      updateResponsePairById,
    ],
  )

  const handleResize = useCallback(
    (id: string, layout: LayoutRect) => {
      const optionMeta = parseOptionBoxId(id)
      const responsePairId = optionMeta
        ? optionMeta.questionId
        : getResponsePairIdFromBoxId(id)

      updateResponsePairById(responsePairId, (question) => {
        if (
          optionMeta &&
          (question.type === "boolean" ||
            question.type === "multiple-choice" ||
            question.type === "multiple-select")
        ) {
          return updatePlacementForOptions(question, optionMeta, layout)
        }

        return updatePlacementForAnswer(question, layout, true)
      })
    },
    [
      updatePlacementForAnswer,
      updatePlacementForOptions,
      updateResponsePairById,
    ],
  )

  return {
    handleDragEnd,
    handleResize,
    updatePlacementForAnswer,
    debouncedUpdatePlacement,
    flush,
  }
}
